<template>
  <article class="checkin-card">
    <div class="checkin-card-main">
      <span class="checkin-avatar" aria-hidden="true">
        <img v-if="businessImage" :src="businessImage" alt="" />
        <span v-else class="checkin-avatar-dot"></span>
      </span>

      <div class="min-w-0 flex-1">
        <h3 class="checkin-business-name">{{ businessName }}</h3>

        <div v-if="businessAddress || rewardPoints" class="checkin-meta-row">
          <span v-if="businessAddress" class="checkin-address" :title="businessAddress">
            <i class="ri-map-pin-2-line checkin-meta-icon" aria-hidden="true"></i>
            <span class="checkin-address-text">{{ businessAddress }}</span>
          </span>
          <span v-if="rewardPoints" class="checkin-points" :aria-label="`${rewardPoints} points`">
            <span class="checkin-points-dot" aria-hidden="true"></span>
            {{ rewardPoints }}
          </span>
        </div>

        <time v-if="timeAgoLabel" class="checkin-date" :datetime="dateTimeValue || undefined">
          <i class="ri-calendar-event-line checkin-meta-icon" aria-hidden="true"></i>
          <span>{{ timeAgoLabel }}</span>
        </time>
        <p
          v-if="comment"
          ref="commentElement"
          class="checkin-comment"
          :class="{ 'checkin-comment-clamped': !commentExpanded }"
        >
          {{ comment }}
        </p>
        <button
          v-if="commentCanToggle"
          class="checkin-comment-toggle"
          type="button"
          :aria-expanded="commentExpanded"
          @click="toggleComment"
        >
          {{ commentExpanded ? 'Show less' : 'Show more' }}
        </button>

      </div>
    </div>
  </article>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { formatRelativeTime, parseApiDate } from '@/utils/date'

const props = defineProps({
  checkin: { type: Object, required: true }
})

const commentElement = ref(null)
const commentExpanded = ref(false)
const commentCanToggle = ref(false)

const businessName = computed(() => {
  const name = String(
    props.checkin.business_name || props.checkin.s_business || props.checkin.title || 'Business'
  ).trim()
  return /^my location$/i.test(name) ? 'Current Location' : name
})
const businessAddress = computed(() => String(
  props.checkin.business_adress || props.checkin.business_address || props.checkin.s_address || props.checkin.address || ''
).trim())
const businessImage = computed(() => String(
  props.checkin.business_image || props.checkin.s_user_img || props.checkin.image || ''
).trim())
const rewardPoints = computed(() => {
  const value = props.checkin.points ?? props.checkin.reward_points ?? props.checkin.checkin_points ?? ''
  return String(value || '').trim()
})

const stripHtml = (value = '') => String(value || '')
  .replace(/<br\s*\/?>/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const comment = computed(() => stripHtml(
  props.checkin.review_comment || props.checkin.comments || props.checkin.description || ''
))
let commentResizeObserver = null

const measureCommentOverflow = async () => {
  if (commentExpanded.value) return
  await nextTick()
  const element = commentElement.value
  commentCanToggle.value = Boolean(element && element.scrollHeight > element.clientHeight + 1)
}

const toggleComment = async () => {
  commentExpanded.value = !commentExpanded.value
  if (!commentExpanded.value) await measureCommentOverflow()
}

onMounted(async () => {
  await measureCommentOverflow()
  if (typeof ResizeObserver !== 'undefined' && commentElement.value) {
    commentResizeObserver = new ResizeObserver(measureCommentOverflow)
    commentResizeObserver.observe(commentElement.value)
  }
})

onBeforeUnmount(() => commentResizeObserver?.disconnect())

watch(comment, async () => {
  commentExpanded.value = false
  await measureCommentOverflow()
})

const parsedDate = computed(() => parseApiDate(props.checkin.created_at || props.checkin.checkin_date))
const dateTimeValue = computed(() => parsedDate.value?.toISOString() || '')
const timeAgoLabel = computed(() => String(
  props.checkin.time_ago ||
  props.checkin.time_Ago ||
  props.checkin.timeAgo ||
  props.checkin.timeago ||
  formatRelativeTime(parsedDate.value) ||
  ''
).trim())

</script>

<style scoped>
.checkin-card {
  overflow: hidden;
  border: 1px solid rgb(226 232 240);
  border-radius: 0.9rem;
  background: white;
  box-shadow: 0 1px 3px rgb(15 23 42 / 0.05);
}

.checkin-card-main {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.9rem;
}

.checkin-avatar {
  display: grid;
  width: 1.75rem;
  height: 1.75rem;
  flex: 0 0 auto;
  place-items: center;
  overflow: hidden;
  border-radius: 9999px;
  background: rgb(245 243 255);
  box-shadow: 0 0 0 4px rgb(250 248 255);
}

.checkin-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.checkin-avatar-dot {
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 9999px;
  background: rgb(124 58 237);
}

.checkin-business-name {
  margin: 0;
  color: rgb(30 41 59);
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.15rem;
}

.checkin-meta-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.3rem;
  color: rgb(100 116 139);
  font-size: 0.72rem;
  line-height: 1rem;
}

.checkin-address {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.22rem;
}

.checkin-address-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.checkin-meta-icon {
  flex: 0 0 auto;
  color: rgb(124 58 237);
  font-size: 0.78rem;
}

.checkin-points {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.18rem;
}

.checkin-points-dot {
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 9999px;
  background: rgb(250 204 21);
}

.checkin-date {
  display: flex;
  align-items: center;
  gap: 0.22rem;
  color: rgb(100 116 139);
  font-size: 0.72rem;
  line-height: 1rem;
}

.checkin-comment {
  margin: 0.8rem 0 0;
  color: rgb(30 41 59);
  font-size: 0.83rem;
  font-weight: 400;
  line-height: 1.3rem;
}

.checkin-comment-clamped {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.checkin-comment-toggle {
  display: block;
  margin-top: 0.25rem;
  color: rgb(37 99 235);
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1rem;
}

.checkin-comment-toggle:hover {
  color: rgb(29 78 216);
  text-decoration: underline;
}

@media (min-width: 640px) {
  .checkin-card-main {
    gap: 0.85rem;
    padding: 1rem;
  }

  .checkin-business-name {
    font-size: 0.88rem;
  }

  .checkin-comment {
    font-size: 0.88rem;
  }
}
</style>
